export type ArchiveRequestType = 'PROJECT_ARCHIVE' | 'MONTH_END_SNAPSHOT' | 'YEAR_END_SNAPSHOT'
export type ArchiveRequestStatus = 'REQUESTED' | 'APPROVED' | 'QUEUED' | 'GENERATING' | 'READY'
export type ArchiveProfileId = 'PROJECT_CLOSE_OUT' | 'MONTH_END_REPORTING' | 'YEAR_END_RECORDS'
export type ArchivePackageOutput = 'REPORT_PACK' | 'ARCHIVE_ZIP' | 'QAQC_ZIP' | 'READ_ONLY_DASHBOARD'

export interface ArchiveProfile {
  id: ArchiveProfileId
  label: string
  description: string
  defaultOutputs: ArchivePackageOutput[]
  intendedUse: string
}

export interface ProjectArchiveStatus {
  jobId: string
  recordsCoveragePct: number
  qaqcCoveragePct: number
  commercialClosePct: number
  handoverClosePct: number
  archiveReadyPct: number
  lastSnapshotAt: string | null
  lastArchiveAt: string | null
  nextSnapshotDueDate: string | null
  recommendedAction: string
  blockingItems: string[]
  supportsReadOnlyDashboard: boolean
  supportsQaqcZip: boolean
}

export interface ArchiveRequest {
  id: string
  jobId: string
  requestType: ArchiveRequestType
  profileId: ArchiveProfileId
  status: ArchiveRequestStatus
  requestedAt: string
  requestedByName: string
  notes: string | null
  outputs: ArchivePackageOutput[]
  targetPeriodLabel: string
  readyAt?: string | null
}

export const ARCHIVE_PROFILE_META: Record<ArchiveProfileId, ArchiveProfile> = {
  PROJECT_CLOSE_OUT: {
    id: 'PROJECT_CLOSE_OUT',
    label: 'Project close-out archive',
    description: 'Full project archive pack for practical completion, handover, and historical read-only access.',
    defaultOutputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    intendedUse: 'Final archive of a completed or archived project.',
  },
  MONTH_END_REPORTING: {
    id: 'MONTH_END_REPORTING',
    label: 'Month-end reporting snapshot',
    description: 'Frozen end-of-month reporting set with read-only dashboard, report pack, and QAQC record zip.',
    defaultOutputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    intendedUse: 'Monthly management reporting and audit traceability.',
  },
  YEAR_END_RECORDS: {
    id: 'YEAR_END_RECORDS',
    label: 'Year-end records snapshot',
    description: 'Year-end archive snapshot for financial close, QAQC, and long-term records retention.',
    defaultOutputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    intendedUse: 'Financial year close and annual compliance evidence pack.',
  },
}

export const ARCHIVE_OUTPUT_META: Record<ArchivePackageOutput, { label: string; description: string }> = {
  REPORT_PACK: {
    label: 'Report pack',
    description: 'Summary reporting output for the selected archive point.',
  },
  ARCHIVE_ZIP: {
    label: 'Archive zip',
    description: 'Self-contained project archive bundle for download and storage.',
  },
  QAQC_ZIP: {
    label: 'QAQC records zip',
    description: 'QAQC evidence set including ITP, hold points, SWMS, permits, inspections, and diaries.',
  },
  READ_ONLY_DASHBOARD: {
    label: 'Read-only archive dashboard',
    description: 'Static dashboard packaged within the archive for offline review on desktop or tablet.',
  },
}

export const MOCK_PROJECT_ARCHIVE_STATUS: ProjectArchiveStatus[] = [
  {
    jobId: 'j1',
    recordsCoveragePct: 82,
    qaqcCoveragePct: 79,
    commercialClosePct: 68,
    handoverClosePct: 54,
    archiveReadyPct: 71,
    lastSnapshotAt: '2026-05-31T17:45:00Z',
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-06-30',
    recommendedAction: 'Month-end snapshot recommended before July delivery push.',
    blockingItems: ['2 BOM lines not yet ordered', 'June QAQC sign-off pack incomplete', 'Progress claim draft not frozen'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
  {
    jobId: 'j2',
    recordsCoveragePct: 64,
    qaqcCoveragePct: 58,
    commercialClosePct: 61,
    handoverClosePct: 22,
    archiveReadyPct: 51,
    lastSnapshotAt: null,
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-06-30',
    recommendedAction: 'Month-end snapshot useful for procurement and commissioning baseline.',
    blockingItems: ['Controller procurement still open', 'QAQC records not yet assembled', 'No month-end diary freeze'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
  {
    jobId: 'j3',
    recordsCoveragePct: 42,
    qaqcCoveragePct: 34,
    commercialClosePct: 47,
    handoverClosePct: 10,
    archiveReadyPct: 33,
    lastSnapshotAt: null,
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-07-31',
    recommendedAction: 'Pre-start baseline snapshot only; full archive not yet appropriate.',
    blockingItems: ['Pre-start QAQC pack not issued', 'Procurement baseline not frozen'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
  {
    jobId: 'j4',
    recordsCoveragePct: 88,
    qaqcCoveragePct: 86,
    commercialClosePct: 74,
    handoverClosePct: 49,
    archiveReadyPct: 78,
    lastSnapshotAt: '2026-06-20T18:10:00Z',
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-06-30',
    recommendedAction: 'Month-end snapshot recommended for Energex reporting and QAQC evidence freeze.',
    blockingItems: ['NCR still open', 'One hold point outstanding release', 'Final commercial reconciliation not complete'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
  {
    jobId: 'j5',
    recordsCoveragePct: 94,
    qaqcCoveragePct: 91,
    commercialClosePct: 86,
    handoverClosePct: 73,
    archiveReadyPct: 86,
    lastSnapshotAt: '2026-05-31T16:30:00Z',
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-06-30',
    recommendedAction: 'Prepare close-out archive pack once final delivery and handover documents land.',
    blockingItems: ['Final lighting completion photos pending', 'Close-out handover checklist not signed'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
  {
    jobId: 'j6',
    recordsCoveragePct: 18,
    qaqcCoveragePct: 14,
    commercialClosePct: 19,
    handoverClosePct: 0,
    archiveReadyPct: 13,
    lastSnapshotAt: null,
    lastArchiveAt: null,
    nextSnapshotDueDate: '2026-07-31',
    recommendedAction: 'No archive action required yet; retain as startup baseline only.',
    blockingItems: ['Work not commenced', 'No QAQC pack yet', 'No commercial baseline freeze'],
    supportsReadOnlyDashboard: true,
    supportsQaqcZip: true,
  },
]

export const MOCK_ARCHIVE_REQUESTS: ArchiveRequest[] = [
  {
    id: 'ar1',
    jobId: 'j4',
    requestType: 'MONTH_END_SNAPSHOT',
    profileId: 'MONTH_END_REPORTING',
    status: 'READY',
    requestedAt: '2026-06-21T09:00:00Z',
    requestedByName: 'Jane Smith',
    notes: 'Freeze June month-end reporting set for Energex progress review.',
    outputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    targetPeriodLabel: 'June 2026 month-end',
    readyAt: '2026-06-21T16:40:00Z',
  },
  {
    id: 'ar2',
    jobId: 'j1',
    requestType: 'MONTH_END_SNAPSHOT',
    profileId: 'MONTH_END_REPORTING',
    status: 'QUEUED',
    requestedAt: '2026-06-24T14:15:00Z',
    requestedByName: 'Sarah Lee',
    notes: 'Required for TMR June reporting and QAQC bundle.',
    outputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    targetPeriodLabel: 'June 2026 month-end',
    readyAt: null,
  },
  {
    id: 'ar3',
    jobId: 'j5',
    requestType: 'PROJECT_ARCHIVE',
    profileId: 'PROJECT_CLOSE_OUT',
    status: 'APPROVED',
    requestedAt: '2026-06-26T10:20:00Z',
    requestedByName: 'Mike Walsh',
    notes: 'Prepare close-out package once final field records are uploaded.',
    outputs: ['REPORT_PACK', 'ARCHIVE_ZIP', 'QAQC_ZIP', 'READ_ONLY_DASHBOARD'],
    targetPeriodLabel: 'Project close-out',
    readyAt: null,
  },
]

export function getJobArchiveStatus(jobId: string) {
  return MOCK_PROJECT_ARCHIVE_STATUS.find((status) => status.jobId === jobId) ?? null
}

export function getArchiveRequestsForJob(jobId: string, requests: ArchiveRequest[] = MOCK_ARCHIVE_REQUESTS) {
  return requests.filter((request) => request.jobId === jobId)
}

export function getArchiveMetrics(scopedJobIds: string[] | null, requests: ArchiveRequest[] = MOCK_ARCHIVE_REQUESTS) {
  const statuses = scopedJobIds === null
    ? MOCK_PROJECT_ARCHIVE_STATUS
    : MOCK_PROJECT_ARCHIVE_STATUS.filter((status) => scopedJobIds.includes(status.jobId))
  const scopedRequests = scopedJobIds === null
    ? requests
    : requests.filter((request) => scopedJobIds.includes(request.jobId))

  return {
    jobsTracked: statuses.length,
    openRequests: scopedRequests.filter((request) => ['REQUESTED', 'APPROVED', 'QUEUED', 'GENERATING'].includes(request.status)).length,
    readyPackages: scopedRequests.filter((request) => request.status === 'READY').length,
    snapshotsDueSoon: statuses.filter((status) => status.nextSnapshotDueDate === '2026-06-30').length,
    nearArchiveReady: statuses.filter((status) => status.archiveReadyPct >= 80).length,
    qaqcAtRisk: statuses.filter((status) => status.qaqcCoveragePct < 80).length,
  }
}

export function archiveReadinessMeta(pct: number) {
  if (pct >= 85) return { label: 'Archive ready', bg: 'bg-green-100', text: 'text-green-700' }
  if (pct >= 65) return { label: 'Needs close-out work', bg: 'bg-amber-100', text: 'text-amber-700' }
  return { label: 'Early stage', bg: 'bg-gray-100', text: 'text-gray-600' }
}
